import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass } from "vue"

const _hoisted_1 = { class: "suga-remote" }
const _hoisted_2 = { class: "year-selector" }
const _hoisted_3 = {
  key: 1,
  class: "loading-container"
}
const _hoisted_4 = {
  key: 2,
  class: "suga-table-container compact"
}
const _hoisted_5 = { class: "suga-table" }
const _hoisted_6 = ["data-suga-id"]
const _hoisted_7 = { class: "action-buttons" }
const _hoisted_8 = {
  key: 0,
  class: "no-suga"
}
const _hoisted_9 = {
  colspan: "4",
  class: "text-center"
}
const _hoisted_10 = {
  key: 3,
  class: "suga-summary"
}
const _hoisted_11 = {
  key: 0,
  class: "deleted-count"
}
const _hoisted_12 = {
  key: 1,
  class: "new-count"
}

import { ref, computed, nextTick, watch } from 'vue';
import axios from 'axios';

// Props
interface Props {
  showButton?: boolean;
}

interface Suga {
  suga_idx?: number;
  suga_biz_gubun: string;
  suga_year: string;
  suga_rank: string;
  suga_money: number | string;
  suga_bonin: number | string;
  isNew?: boolean;
  isDeleted?: boolean;
  isModified?: boolean;
  tempId?: string;
  isValid?: boolean;
  originalData?: {
    suga_rank: string;
    suga_money: number | string;
    suga_bonin: number | string;
  };
}


export default /*@__PURE__*/_defineComponent({
  __name: 'SugaRemote',
  props: {
    showButton: { type: Boolean, default: false }
  },
  emits: ["saved", "updated"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

// Emits
const emit = __emit;

const dialog = ref(false);
const selectedYear = ref(new Date().getFullYear());
const sugaList = ref<Suga[]>([]);
const originalSugaList = ref<Suga[]>([]);
const loading = ref(false);
const saving = ref(false);
const isFallback = ref(false);
const actualYear = ref<string | null>(null);
const snackbar = ref({ 
  show: false, 
  message: '', 
  color: 'success',
  icon: 'mdi-check-circle'
});

const yearOptions = computed(() => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);
});

// 활성 수가 개수 (삭제 예정 제외)
const activeSugaCount = computed(() => {
  return sugaList.value.filter(s => !s.isDeleted).length;
});

// 삭제 예정 개수
const deletedCount = computed(() => {
  return sugaList.value.filter(s => s.isDeleted && !s.isNew).length;
});

// 신규 개수
const newCount = computed(() => {
  return sugaList.value.filter(s => s.isNew && !s.isDeleted).length;
});

// 변경사항 있는지 확인
const hasChanges = computed(() => {
  // 신규 추가된 항목이 있는지
  const hasNew = sugaList.value.some(s => s.isNew && !s.isDeleted);
  
  // 삭제 예정 항목이 있는지
  const hasDeleted = sugaList.value.some(s => s.isDeleted && !s.isNew);
  
  // 수정된 항목이 있는지
  const hasModified = sugaList.value.some(s => {
    if (s.isNew || s.isDeleted) return false;
    if (!s.originalData) return false;
    
    const currentMoney = parseMoneyValue(s.suga_money);
    const originalMoney = parseMoneyValue(s.originalData.suga_money);
    const currentBonin = parseMoneyValue(s.suga_bonin);
    const originalBonin = parseMoneyValue(s.originalData.suga_bonin);
    
    return s.suga_rank !== s.originalData.suga_rank ||
           currentMoney !== originalMoney ||
           currentBonin !== originalBonin;
  });
  
  return hasNew || hasDeleted || hasModified;
});

const loadSuga = async () => {
  loading.value = true;
  isFallback.value = false;
  actualYear.value = null;
  
  try {
    const response = await axios.get(`/api/calc/suga/${selectedYear.value}`);
    if (response.data.success) {
      const data = response.data.data || [];
      
      // Fallback 정보 처리
      if (response.data.isFallback) {
        isFallback.value = true;
        actualYear.value = response.data.year;
      }
      
      sugaList.value = data.map((s: Suga) => ({
        ...s,
        suga_money: formatMoney(s.suga_money || 0),
        suga_bonin: formatMoney(s.suga_bonin || 0),
        isNew: false,
        isDeleted: false,
        isModified: false,
        isValid: true,
        originalData: {
          suga_rank: s.suga_rank,
          suga_money: s.suga_money,
          suga_bonin: s.suga_bonin
        }
      }));
      
      // 원본 데이터 저장
      originalSugaList.value = JSON.parse(JSON.stringify(sugaList.value));
    } else {
      sugaList.value = [];
    }
  } catch (error: any) {
    console.error('수가 조회 실패:', error);
    
    if (error.response?.status === 404) {
      // 데이터가 없는 경우
      sugaList.value = [];
      showSnackbar(`${selectedYear.value}년 수가 데이터가 없습니다. 새로 추가해주세요.`, 'info', 'mdi-information');
    } else {
      showSnackbar('수가 조회에 실패했습니다', 'error', 'mdi-alert-circle');
    }
  } finally {
    loading.value = false;
  }
};

const addSuga = () => {
  const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  const newSuga: Suga = {
    suga_biz_gubun: '방문요양',
    suga_year: selectedYear.value.toString(),
    suga_rank: '',
    suga_money: '',
    suga_bonin: '',
    isNew: true,
    isDeleted: false,
    isModified: false,
    tempId,
    isValid: false
  };
  
  sugaList.value.push(newSuga);
  
  // 새로 추가된 항목에 포커스
  nextTick(() => {
    const rankInput = document.querySelector(`[data-suga-id="${tempId}"] .rank-input input`) as HTMLInputElement;
    if (rankInput) {
      rankInput.focus();
    }
  });
};

const validateSuga = (suga: Suga) => {
  const rank = suga.suga_rank?.trim();
  suga.isValid = Boolean(rank && rank.length > 0);
  
  if (rank && rank.length > 50) {
    suga.suga_rank = rank.substring(0, 50);
    showSnackbar('시간은 50자 이내로 입력해주세요', 'warning', 'mdi-alert');
  }
};

// 금액 포맷팅 함수
const formatMoney = (amount: number | string): string => {
  if (!amount && amount !== 0) return '';
  const numericValue = typeof amount === 'string' ? parseFloat(amount.replace(/[^\d]/g, '')) : amount;
  if (isNaN(numericValue)) return '';
  return new Intl.NumberFormat('ko-KR').format(numericValue);
};

// 금액 입력 필드 포커스 이벤트 (컴마 제거)
const onMoneyFocus = (event: FocusEvent, suga: Suga) => {
  const input = event.target as HTMLInputElement;
  const value = input.value;
  const numericValue = value.replace(/,/g, '');
  input.value = numericValue;
  suga.suga_money = numericValue;
};

// 금액 입력 필드 포커스 아웃 이벤트 (컴마 추가)
const onMoneyBlur = (event: FocusEvent, suga: Suga) => {
  const input = event.target as HTMLInputElement;
  const value = input.value;
  const numericValue = value.replace(/[^\d]/g, '');
  
  if (numericValue) {
    const formattedValue = formatMoney(parseInt(numericValue));
    input.value = formattedValue;
    suga.suga_money = formattedValue;
  } else {
    input.value = '';
    suga.suga_money = '';
  }
};

// 금액 입력 필드 입력 이벤트 (숫자만 허용)
const onMoneyInput = (event: Event, suga: Suga) => {
  const input = event.target as HTMLInputElement;
  let value = input.value;
  value = value.replace(/[^\d]/g, '');
  
  if (value.length > 12) {
    value = value.slice(0, 12);
  }
  
  input.value = value;
  suga.suga_money = value;
};

// 본인부담금 입력 필드 포커스 이벤트 (컴마 제거)
const onBoninFocus = (event: FocusEvent, suga: Suga) => {
  const input = event.target as HTMLInputElement;
  const value = input.value;
  const numericValue = value.replace(/,/g, '');
  input.value = numericValue;
  suga.suga_bonin = numericValue;
};

// 본인부담금 입력 필드 포커스 아웃 이벤트 (컴마 추가)
const onBoninBlur = (event: FocusEvent, suga: Suga) => {
  const input = event.target as HTMLInputElement;
  const value = input.value;
  const numericValue = value.replace(/[^\d]/g, '');
  
  if (numericValue) {
    const formattedValue = formatMoney(parseInt(numericValue));
    input.value = formattedValue;
    suga.suga_bonin = formattedValue;
  } else {
    input.value = '';
    suga.suga_bonin = '';
  }
};

// 본인부담금 입력 필드 입력 이벤트 (숫자만 허용)
const onBoninInput = (event: Event, suga: Suga) => {
  const input = event.target as HTMLInputElement;
  let value = input.value;
  value = value.replace(/[^\d]/g, '');
  
  if (value.length > 12) {
    value = value.slice(0, 12);
  }
  
  input.value = value;
  suga.suga_bonin = value;
};

const removeSuga = (suga: Suga) => {
  if (suga.isNew) {
    // 신규 추가된 항목은 바로 삭제
    const index = sugaList.value.indexOf(suga);
    sugaList.value.splice(index, 1);
    showSnackbar('수가를 삭제했습니다', 'info', 'mdi-delete');
  } else {
    // 기존 항목은 삭제 표시
    suga.isDeleted = true;
    showSnackbar('저장 시 삭제됩니다. 취소하려면 복원 버튼을 클릭하세요.', 'warning', 'mdi-delete-outline');
  }
};

// 삭제 취소 (복원)
const restoreSuga = (suga: Suga) => {
  suga.isDeleted = false;
  showSnackbar('삭제가 취소되었습니다', 'success', 'mdi-undo');
};

// 숫자 문자열을 숫자로 변환하는 함수
const parseMoneyValue = (value: number | string): number => {
  if (typeof value === 'number') return value;
  if (!value) return 0;
  return parseFloat(String(value).replace(/[^\d]/g, '')) || 0;
};

const saveSuga = async () => {
  // 유효성 검사
  const invalidSuga = sugaList.value.filter(s => 
    !s.isDeleted && !s.suga_rank?.trim()
  );
  
  if (invalidSuga.length > 0) {
    showSnackbar('시간을 입력해주세요', 'error', 'mdi-alert-circle');
    return;
  }

  saving.value = true;
  try {
    let successCount = 0;
    let errorCount = 0;
    const errors: string[] = [];

    // 1. 삭제 처리
    const deletes = sugaList.value.filter(s => s.isDeleted && s.suga_idx);
    for (const s of deletes) {
      try {
        await axios.delete(`/api/calc/suga/${s.suga_idx}`);
        successCount++;
      } catch (error) {
        errorCount++;
        errors.push(`삭제 실패: ${s.suga_rank}`);
      }
    }

    // 2. 추가 처리
    const inserts = sugaList.value.filter(s => s.isNew && !s.isDeleted && s.suga_rank.trim());
    for (const s of inserts) {
      try {
        await axios.post('/api/calc/suga', {
          suga_biz_gubun: s.suga_biz_gubun || '방문요양',
          suga_year: selectedYear.value.toString(),
          suga_rank: s.suga_rank.trim(),
          suga_money: parseMoneyValue(s.suga_money),
          suga_bonin: parseMoneyValue(s.suga_bonin)
        });
        successCount++;
      } catch (error) {
        errorCount++;
        errors.push(`추가 실패: ${s.suga_rank}`);
      }
    }

    // 3. 수정 처리
    const updates = sugaList.value.filter(s => {
      if (s.isNew || s.isDeleted || !s.suga_idx) return false;
      if (!s.originalData) return false;
      
      const currentMoney = parseMoneyValue(s.suga_money);
      const originalMoney = parseMoneyValue(s.originalData.suga_money);
      const currentBonin = parseMoneyValue(s.suga_bonin);
      const originalBonin = parseMoneyValue(s.originalData.suga_bonin);
      
      return s.suga_rank !== s.originalData.suga_rank ||
             currentMoney !== originalMoney ||
             currentBonin !== originalBonin;
    });
    
    for (const s of updates) {
      try {
        await axios.put(`/api/calc/suga/${s.suga_idx}`, {
          suga_biz_gubun: s.suga_biz_gubun || '방문요양',
          suga_year: selectedYear.value.toString(),
          suga_rank: s.suga_rank.trim(),
          suga_money: parseMoneyValue(s.suga_money),
          suga_bonin: parseMoneyValue(s.suga_bonin)
        });
        successCount++;
      } catch (error) {
        errorCount++;
        errors.push(`수정 실패: ${s.suga_rank}`);
      }
    }

    if (errorCount > 0) {
      showSnackbar(`일부 저장 실패: ${errorCount}건 (성공: ${successCount}건)`, 'warning', 'mdi-alert');
      console.error('저장 오류:', errors);
    } else if (successCount > 0) {
      showSnackbar(`저장 완료! (${successCount}건 처리)`, 'success', 'mdi-check-circle');
      emit('saved');
      emit('updated', selectedYear.value);
    } else {
      showSnackbar('변경사항이 없습니다', 'info', 'mdi-information');
    }
    
    // 데이터 새로고침
    await loadSuga();
  } catch (error) {
    console.error('저장 실패:', error);
    showSnackbar('저장 중 오류가 발생했습니다', 'error', 'mdi-alert-circle');
  } finally {
    saving.value = false;
  }
};

const showSnackbar = (message: string, color: string = 'success', icon: string = 'mdi-check-circle') => {
  snackbar.value = { show: true, message, color, icon };
};

// 다이얼로그 열릴 때 데이터 로드
const openDialog = () => {
  dialog.value = true;
  loadSuga();
};

// 다이얼로그가 닫힐 때 상태 초기화
watch(dialog, (newValue) => {
  if (!newValue) {
    // 다이얼로그 닫힐 때 상태 초기화
    isFallback.value = false;
    actualYear.value = null;
  }
});

// 외부에서 접근 가능하도록 노출
__expose({
  openDialog,
  loadSuga
});

return (_ctx: any,_cache: any) => {
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_spacer = _resolveComponent("v-spacer")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_alert = _resolveComponent("v-alert")!
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card_actions = _resolveComponent("v-card-actions")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_dialog = _resolveComponent("v-dialog")!
  const _component_v_snackbar = _resolveComponent("v-snackbar")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (__props.showButton)
      ? (_openBlock(), _createBlock(_component_v_btn, {
          key: 0,
          color: "primary",
          variant: "elevated",
          onClick: openDialog,
          "prepend-icon": "mdi-cash-multiple"
        }, {
          default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
            _createTextVNode(" 수가 관리 ", -1)
          ]))]),
          _: 1
        }))
      : _createCommentVNode("", true),
    _createVNode(_component_v_dialog, {
      modelValue: dialog.value,
      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((dialog).value = $event)),
      "max-width": "600px"
    }, {
      default: _withCtx(() => [
        _createVNode(_component_v_card, null, {
          default: _withCtx(() => [
            _createVNode(_component_v_card_title, { class: "dialog-title" }, {
              default: _withCtx(() => [
                _cache[8] || (_cache[8] = _createTextVNode(" 수가 관리 ", -1)),
                _createVNode(_component_v_spacer),
                _createVNode(_component_v_btn, {
                  icon: "",
                  variant: "text",
                  onClick: _cache[0] || (_cache[0] = ($event: any) => (dialog.value = false))
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_icon, null, {
                      default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                        _createTextVNode("mdi-close", -1)
                      ]))]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }),
            _createVNode(_component_v_card_text, { class: "dialog-content" }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_2, [
                  _createVNode(_component_v_select, {
                    modelValue: selectedYear.value,
                    "onUpdate:modelValue": [
                      _cache[1] || (_cache[1] = ($event: any) => ((selectedYear).value = $event)),
                      loadSuga
                    ],
                    items: yearOptions.value,
                    label: "연도 선택",
                    variant: "outlined",
                    density: "compact",
                    "hide-details": "",
                    class: "year-select",
                    disabled: loading.value
                  }, null, 8, ["modelValue", "items", "disabled"]),
                  _createVNode(_component_v_btn, {
                    color: "primary",
                    variant: "outlined",
                    onClick: addSuga,
                    "prepend-icon": "mdi-plus",
                    class: "add-btn",
                    disabled: loading.value
                  }, {
                    default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                      _createTextVNode(" 추가 ", -1)
                    ]))]),
                    _: 1
                  }, 8, ["disabled"])
                ]),
                (isFallback.value && actualYear.value)
                  ? (_openBlock(), _createBlock(_component_v_alert, {
                      key: 0,
                      type: "info",
                      variant: "tonal",
                      density: "compact",
                      class: "fallback-alert"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(selectedYear.value) + "년 수가 데이터가 없어 " + _toDisplayString(actualYear.value) + "년 데이터를 불러왔습니다. ", 1),
                        _createElementVNode("strong", null, "저장 시 " + _toDisplayString(selectedYear.value) + "년 데이터로 저장됩니다.", 1)
                      ]),
                      _: 1
                    }))
                  : _createCommentVNode("", true),
                (loading.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                      _createVNode(_component_v_progress_circular, {
                        indeterminate: "",
                        color: "primary",
                        size: "40"
                      }),
                      _cache[10] || (_cache[10] = _createElementVNode("span", null, "수가 데이터를 불러오는 중...", -1))
                    ]))
                  : (_openBlock(), _createElementBlock("div", _hoisted_4, [
                      _createElementVNode("table", _hoisted_5, [
                        _cache[15] || (_cache[15] = _createElementVNode("thead", null, [
                          _createElementVNode("tr", null, [
                            _createElementVNode("th", null, "시간"),
                            _createElementVNode("th", null, "수가"),
                            _createElementVNode("th", null, "본인부담금"),
                            _createElementVNode("th", null, "작업")
                          ])
                        ], -1)),
                        _createElementVNode("tbody", null, [
                          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(sugaList.value, (suga) => {
                            return (_openBlock(), _createElementBlock("tr", {
                              key: suga.suga_idx || suga.tempId,
                              "data-suga-id": suga.tempId,
                              class: _normalizeClass({ 'is-deleted': suga.isDeleted, 'is-new': suga.isNew })
                            }, [
                              _createElementVNode("td", null, [
                                _createVNode(_component_v_text_field, {
                                  modelValue: suga.suga_rank,
                                  "onUpdate:modelValue": ($event: any) => ((suga.suga_rank) = $event),
                                  variant: "outlined",
                                  density: "compact",
                                  "hide-details": "",
                                  placeholder: "시간 (예: 60분)",
                                  class: "rank-input",
                                  disabled: suga.isDeleted,
                                  onInput: ($event: any) => (validateSuga(suga))
                                }, null, 8, ["modelValue", "onUpdate:modelValue", "disabled", "onInput"])
                              ]),
                              _createElementVNode("td", null, [
                                _createVNode(_component_v_text_field, {
                                  modelValue: suga.suga_money,
                                  "onUpdate:modelValue": ($event: any) => ((suga.suga_money) = $event),
                                  type: "text",
                                  variant: "outlined",
                                  density: "compact",
                                  "hide-details": "",
                                  placeholder: "수가",
                                  class: "money-input",
                                  disabled: suga.isDeleted,
                                  onFocus: ($event: any) => (onMoneyFocus($event, suga)),
                                  onBlur: ($event: any) => (onMoneyBlur($event, suga)),
                                  onInput: ($event: any) => (onMoneyInput($event, suga))
                                }, null, 8, ["modelValue", "onUpdate:modelValue", "disabled", "onFocus", "onBlur", "onInput"])
                              ]),
                              _createElementVNode("td", null, [
                                _createVNode(_component_v_text_field, {
                                  modelValue: suga.suga_bonin,
                                  "onUpdate:modelValue": ($event: any) => ((suga.suga_bonin) = $event),
                                  type: "text",
                                  variant: "outlined",
                                  density: "compact",
                                  "hide-details": "",
                                  placeholder: "본인부담금",
                                  class: "bonin-input",
                                  disabled: suga.isDeleted,
                                  onFocus: ($event: any) => (onBoninFocus($event, suga)),
                                  onBlur: ($event: any) => (onBoninBlur($event, suga)),
                                  onInput: ($event: any) => (onBoninInput($event, suga))
                                }, null, 8, ["modelValue", "onUpdate:modelValue", "disabled", "onFocus", "onBlur", "onInput"])
                              ]),
                              _createElementVNode("td", null, [
                                _createElementVNode("div", _hoisted_7, [
                                  (!suga.isDeleted)
                                    ? (_openBlock(), _createBlock(_component_v_btn, {
                                        key: 0,
                                        color: "error",
                                        variant: "outlined",
                                        onClick: ($event: any) => (removeSuga(suga)),
                                        size: "small",
                                        icon: ""
                                      }, {
                                        default: _withCtx(() => [
                                          _createVNode(_component_v_icon, null, {
                                            default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                                              _createTextVNode("mdi-delete", -1)
                                            ]))]),
                                            _: 1
                                          })
                                        ]),
                                        _: 1
                                      }, 8, ["onClick"]))
                                    : (_openBlock(), _createBlock(_component_v_btn, {
                                        key: 1,
                                        color: "success",
                                        variant: "outlined",
                                        onClick: ($event: any) => (restoreSuga(suga)),
                                        size: "small",
                                        icon: ""
                                      }, {
                                        default: _withCtx(() => [
                                          _createVNode(_component_v_icon, null, {
                                            default: _withCtx(() => [...(_cache[12] || (_cache[12] = [
                                              _createTextVNode("mdi-undo", -1)
                                            ]))]),
                                            _: 1
                                          })
                                        ]),
                                        _: 1
                                      }, 8, ["onClick"]))
                                ])
                              ])
                            ], 10, _hoisted_6))
                          }), 128)),
                          (sugaList.value.length === 0)
                            ? (_openBlock(), _createElementBlock("tr", _hoisted_8, [
                                _createElementVNode("td", _hoisted_9, [
                                  _createVNode(_component_v_icon, { color: "grey" }, {
                                    default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                                      _createTextVNode("mdi-cash-off", -1)
                                    ]))]),
                                    _: 1
                                  }),
                                  _cache[14] || (_cache[14] = _createElementVNode("span", null, "수가 데이터가 없습니다", -1))
                                ])
                              ]))
                            : _createCommentVNode("", true)
                        ])
                      ])
                    ])),
                (sugaList.value.length > 0 && !loading.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                      _createElementVNode("span", null, "총 " + _toDisplayString(activeSugaCount.value) + "개 수가", 1),
                      (deletedCount.value > 0)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_11, "(삭제 예정: " + _toDisplayString(deletedCount.value) + "개)", 1))
                        : _createCommentVNode("", true),
                      (newCount.value > 0)
                        ? (_openBlock(), _createElementBlock("span", _hoisted_12, "(신규: " + _toDisplayString(newCount.value) + "개)", 1))
                        : _createCommentVNode("", true)
                    ]))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            }),
            _createVNode(_component_v_card_actions, { class: "dialog-actions" }, {
              default: _withCtx(() => [
                _createVNode(_component_v_spacer),
                _createVNode(_component_v_btn, {
                  variant: "text",
                  onClick: _cache[2] || (_cache[2] = ($event: any) => (dialog.value = false)),
                  disabled: saving.value
                }, {
                  default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                    _createTextVNode("취소", -1)
                  ]))]),
                  _: 1
                }, 8, ["disabled"]),
                _createVNode(_component_v_btn, {
                  color: "primary",
                  variant: "elevated",
                  onClick: saveSuga,
                  loading: saving.value,
                  disabled: loading.value || !hasChanges.value
                }, {
                  default: _withCtx(() => [
                    _createVNode(_component_v_icon, { class: "mr-1" }, {
                      default: _withCtx(() => [...(_cache[17] || (_cache[17] = [
                        _createTextVNode("mdi-content-save", -1)
                      ]))]),
                      _: 1
                    }),
                    _cache[18] || (_cache[18] = _createTextVNode(" 저장 ", -1))
                  ]),
                  _: 1
                }, 8, ["loading", "disabled"])
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _: 1
    }, 8, ["modelValue"]),
    _createVNode(_component_v_snackbar, {
      modelValue: snackbar.value.show,
      "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((snackbar.value.show) = $event)),
      color: snackbar.value.color,
      timeout: 3000
    }, {
      actions: _withCtx(() => [
        _createVNode(_component_v_btn, {
          variant: "text",
          onClick: _cache[4] || (_cache[4] = ($event: any) => (snackbar.value.show = false))
        }, {
          default: _withCtx(() => [...(_cache[19] || (_cache[19] = [
            _createTextVNode("닫기", -1)
          ]))]),
          _: 1
        })
      ]),
      default: _withCtx(() => [
        _createTextVNode(_toDisplayString(snackbar.value.message) + " ", 1)
      ]),
      _: 1
    }, 8, ["modelValue", "color"])
  ]))
}
}

})